import { useState } from 'react';
import { confirmDisconnect } from './disconnect-confirm.store';
import { AlertTriangle, Loader2, MoreHorizontal, Trash2 } from 'lucide-react';
import { useDisconnectIntegration, useIntegrations } from '@/hooks';
import { integrationService } from '@/services';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
	META_CAPABILITY_LABELS,
	canGainLikeViaFacebook,
	findMetaChannels,
	groupMetaChannels,
	hasNoCapabilities,
	needsReconnect,
	metaAuthOptionsFor,
	readMetaChannels,
	type MetaAuthType,
	type MetaChannel
} from '../meta-contract';
import type { Integration } from '../types';
import { useMetaAuthTypes } from '../hooks/use-meta-auth-types';
import { useMetaConnect } from '../hooks/use-meta-connect';


interface MetaContentProps {
	integration: Integration;
}

// ---------------------------------------------------------------------------
// Connect buttons
// ---------------------------------------------------------------------------

interface ConnectButtonsProps {
	availableAuthTypes: MetaAuthType[];
	pendingAuthType: MetaAuthType | null;
	onConnect: (authType: MetaAuthType) => void;
	compact?: boolean;
}

/**
 * One button per consent route this deployment can actually start.
 *
 * Meta's two products are separate consent screens on separate hosts, and a brand may only qualify
 * for one — an Instagram account with no Facebook Page is invisible to Facebook Login, and a
 * Facebook Page is unreachable through Instagram Login. Which applies is the customer's call, so
 * both are offered where both are configured; the helper text is what makes the choice answerable
 * without reading Meta's docs.
 *
 * `availableAuthTypes` comes from machine, never from this build's own list: a route can be
 * switched off per environment, and rendering a button for one that is off returns a 500.
 */
const MetaConnectButtons = ({
	availableAuthTypes,
	pendingAuthType,
	onConnect,
	compact = false
}: ConnectButtonsProps) => (
	<div className='flex flex-col gap-3'>
		{metaAuthOptionsFor(availableAuthTypes).map((option) => (
			<div key={option.authType} className='flex flex-col gap-1'>
				<Button
					variant='outline'
					className='justify-center cursor-pointer'
					disabled={pendingAuthType !== null}
					onClick={() => onConnect(option.authType)}
				>
					{pendingAuthType === option.authType && <Loader2 className='size-4 animate-spin' />}
					{option.label}
				</Button>
				{!compact && <p className='text-xs text-muted-foreground'>{option.helper}</p>}
			</div>
		))}
	</div>
);

// ---------------------------------------------------------------------------
// One connected account
// ---------------------------------------------------------------------------

const CapabilityChips = ({ channel }: { channel: MetaChannel }) => {
	// FIRST, above the permissions warning. A channel can be short of permissions and have a dying
	// token at once, and the two ask for different things — re-authorise with more ticked, versus
	// re-authorise at all. Only one of them has a deadline.
	if (needsReconnect(channel)) {
		return (
			<div className='flex items-center gap-1 text-amber-600'>
				<AlertTriangle className='size-3 shrink-0' />
				<span className='text-[11px]'>Access is expiring — reconnect to keep this account working</span>
			</div>
		);
	}

	if (hasNoCapabilities(channel)) {
		return (
			<div className='flex items-center gap-1 text-amber-600'>
				<AlertTriangle className='size-3 shrink-0' />
				<span className='text-[11px]'>No permissions granted — no rule can act on this account</span>
			</div>
		);
	}

	return (
		<div className='flex flex-wrap items-center gap-1'>
			{channel.capabilities.map((capability) => (
				<span key={capability} className='text-[11px] leading-none px-1.5 py-1 rounded-full bg-muted text-muted-foreground'>
					{META_CAPABILITY_LABELS[capability]}
				</span>
			))}
		</div>
	);
};

const ChannelAvatar = ({ channel }: { channel: MetaChannel }) => {
	// Meta omits the picture on accounts with no profile photo, and the URLs it does return are
	// signed and can expire — so the initial is a real fallback, not a loading placeholder.
	const [failed, setFailed] = useState(false);
	const initial = (channel.name || channel.handle || '?').charAt(0).toUpperCase();

	if (!channel.pictureUrl || failed) {
		return (
			<div className='size-8 rounded-full bg-muted flex items-center justify-center shrink-0'>
				<span className='text-xs font-medium text-muted-foreground'>{initial}</span>
			</div>
		);
	}

	return (
		<img
			src={channel.pictureUrl}
			alt={channel.name}
			onError={() => setFailed(true)}
			className='size-8 rounded-full object-cover shrink-0'
		/>
	);
};

interface ChannelRowProps {
	channel: MetaChannel;
	isRemoving: boolean;
	onRemove: (channel: MetaChannel) => void;
}

const MetaChannelRow = ({ channel, isRemoving, onRemove }: ChannelRowProps) => (
	<div className='flex items-start gap-3 p-3 border rounded-lg'>
		<ChannelAvatar channel={channel} />

		<div className='flex flex-col gap-1.5 flex-1 min-w-0'>
			<div className='flex items-baseline gap-1.5 min-w-0'>
				<span className='text-sm font-medium truncate'>{channel.name}</span>
				<span className='text-xs text-muted-foreground truncate'>@{channel.handle}</span>
			</div>

			<CapabilityChips channel={channel} />

			{canGainLikeViaFacebook(channel) && (
				<p className='text-[11px] text-muted-foreground'>
					Connect this account through its Facebook Page to also like comments — Meta has no like permission on the
					Instagram-only route.
				</p>
			)}
		</div>

		<Button
			variant='ghost'
			size='icon-sm'
			aria-label={`Remove ${channel.name}`}
			disabled={isRemoving}
			onClick={() => onRemove(channel)}
			className='cursor-pointer'
		>
			{isRemoving ? <Loader2 className='size-4 animate-spin' /> : <Trash2 className='size-4' />}
		</Button>
	</div>
);

// ---------------------------------------------------------------------------
// Container
// ---------------------------------------------------------------------------

export const MetaContent = ({ integration }: MetaContentProps) => {
	const [removingCredentialId, setRemovingCredentialId] = useState<string | null>(null);
	const [checkingStatus, setCheckingStatus] = useState(false);
	const disconnectMutation = useDisconnectIntegration();
	const integrationsQuery = useIntegrations();
	const { authTypes: availableAuthTypes, failed: authTypesFailed, reload: reloadAuthTypes } = useMetaAuthTypes();
	const { pendingAuthType, connect, clearPending } = useMetaConnect({ refetchIntegrations: integrationsQuery.refetch });

	const channels = readMetaChannels(integration.authMetaData);
	const groups = groupMetaChannels(channels);

	/**
	 * The customer saying "I'm done" — the fallback for when neither the popup's message nor its
	 * closing reached us. Also the retry for the connect options when their read failed.
	 */
	const checkStatus = async () => {
		setCheckingStatus(true);
		if (authTypesFailed) await reloadAuthTypes();

		let result: Awaited<ReturnType<typeof integrationsQuery.refetch>> | null = null;
		try {
			result = await integrationsQuery.refetch();
		} catch (err) {
			console.error('[MetaContent.checkStatus] integrations refetch failed', err);
		}
		setCheckingStatus(false);

		if (!result) {
			toast.error('Could not reach Kim to check the connection.');
			return;
		}

		if (findMetaChannels(result.data).length > 0) {
			clearPending();
			toast.success('Meta is connected.');
			return;
		}

		toast.error('No connection yet. Finish the Meta window, then check again.');
	};

	const handleRemove = async (channel: MetaChannel) => {
		setRemovingCredentialId(channel.credentialId);
		try {
			await integrationService.removeMetaCredential(channel.credentialId);
			await integrationsQuery.refetch();
			toast.success(`Removed ${channel.name}`);
		} catch (err) {
			console.error('[MetaContent.handleRemove] removeMetaCredential failed', { credentialId: channel.credentialId, err });
			toast.error(getApiErrorMessage(err, `Failed to remove ${channel.name}`));
		} finally {
			setRemovingCredentialId(null);
		}
	};

	const handleDisconnectAll = async () => {
		if (disconnectMutation.isPending) return;
		if (!(await confirmDisconnect(integration.name))) return;
		disconnectMutation.mutate(integration.id);
	};

	/** Buttons, a loading placeholder, a retry, or an explanation — never a button that cannot work. */
	const renderConnectOptions = (compact = false) => {
		if (authTypesFailed) {
			return (
				<div className='flex flex-col gap-2'>
					<p className='text-sm text-muted-foreground'>Could not load the Meta connection options.</p>
					<Button variant='outline' size='sm' onClick={() => void reloadAuthTypes()}>
						Retry
					</Button>
				</div>
			);
		}

		if (availableAuthTypes === null) {
			return <Skeleton className='h-9 w-full rounded-md' />;
		}

		if (availableAuthTypes.length === 0) {
			return (
				<p className='text-sm text-muted-foreground'>
					Meta connections aren&apos;t enabled on this environment yet. Contact support to have them turned on.
				</p>
			);
		}

		return (
			<div className='flex flex-col gap-2'>
				<MetaConnectButtons
					availableAuthTypes={availableAuthTypes}
					pendingAuthType={pendingAuthType}
					onConnect={(authType) => void connect(authType)}
					compact={compact}
				/>

				{/*
				 * Only while a connect is in flight. Offering it before the customer has opened Meta
				 * would just report "not connected" for a connection nobody has started.
				 */}
				{pendingAuthType && (
					<div className='flex flex-col gap-1'>
						<Button variant='outline' size='sm' onClick={checkStatus} disabled={checkingStatus}>
							{checkingStatus ? <Loader2 className='size-3.5 animate-spin' /> : null}
							Check connection status
						</Button>
						<p className='text-xs text-muted-foreground'>
							Finish in the Meta window. This updates on its own when you come back — use the button if it
							doesn&apos;t.
						</p>
					</div>
				)}
			</div>
		);
	};

	if (!integration.isConnected || channels.length === 0) {
		return (
			<div className='flex flex-col gap-4 px-5 pb-5'>
				<div className='flex flex-col gap-1'>
					<h3 className='text-sm font-semibold'>Connect a Meta account</h3>
					<p className='text-sm text-muted-foreground'>
						Kim moderates comments on the Facebook Pages and Instagram accounts you connect here. You authorize in
						Meta — Kim never sees your Meta password.
					</p>
				</div>

				{renderConnectOptions()}
			</div>
		);
	}

	return (
		<div className='flex flex-col gap-4 px-5 pb-5'>
			<div className='flex items-start justify-between'>
				<div className='flex flex-col gap-0.5'>
					<h3 className='text-sm'>Connected accounts</h3>
					<p className='text-[10px] italic text-muted-foreground'>
						What each account can do is set by the permissions Meta granted at connect time.
					</p>
				</div>
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant='ghost' size='icon-sm' aria-label='Meta connection options'>
							<MoreHorizontal className='size-4' />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align='end'>
						<DropdownMenuItem variant='destructive' onClick={handleDisconnectAll} disabled={disconnectMutation.isPending}>
							{disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect all'}
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{groups.map((group) => (
				<div key={group.platform} className='flex flex-col gap-2'>
					<p className='text-xs text-muted-foreground font-medium'>{group.label}</p>
					{group.channels.map((channel) => (
						<MetaChannelRow
							key={`${channel.platform}:${channel.channelId}`}
							channel={channel}
							isRemoving={removingCredentialId === channel.credentialId}
							onRemove={handleRemove}
						/>
					))}
				</div>
			))}

			<Separator />

			<div className='flex flex-col gap-3'>
				<p className='text-xs text-muted-foreground font-medium'>Add another account</p>
				{renderConnectOptions(true)}
			</div>
		</div>
	);
};
